import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';
import { useToast } from '../components/layout/ToastProvider';
import FormModal from '../components/common/FormModal';
import ConfirmModal from '../components/common/ConfirmModal';
import { motion, AnimatePresence } from 'framer-motion';

const { FiUsers, FiChevronLeft, FiPlus, FiTrash2, FiSearch, FiMoreVertical, FiMail, FiCheckCircle, FiClock } = FiIcons;

export default function CourseEnrollments() {
  const { id } = useParams();
  const { addToast } = useToast();
  
  const [course, setCourse] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [availableClients, setAvailableClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
  const [isUnenrollModalOpen, setIsUnenrollModalOpen] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [{ data: courseData }, { data: enrollData }, { data: clientsData }] = await Promise.all([
        supabase.from('courses_2024').select('*').eq('id', id).single(),
        supabase.from('enrollments_2024')
          .select(`*, client:clients_2024(*)`)
          .eq('course_id', id)
          .order('enrolled_at', { ascending: false }),
        supabase.from('clients_2024').select('*')
      ]);

      setCourse(courseData);
      setEnrollments(enrollData || []);
      
      // Filter out clients already enrolled
      const enrolledClientIds = new Set(enrollData?.map(e => e.client_id) || []);
      setAvailableClients(clientsData?.filter(c => !enrolledClientIds.has(c.id)) || []);
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (e) => {
    setModalLoading(true);
    const clientId = new FormData(e.target).get('client_id');
    
    try {
      const { data, error } = await supabase
        .from('enrollments_2024')
        .insert([{ 
          course_id: id, 
          client_id: clientId,
          status: 'active'
        }])
        .select(`*, client:clients_2024(*)`);

      if (error) throw error;
      
      setEnrollments([data[0], ...enrollments]);
      setAvailableClients(availableClients.filter(c => c.id !== clientId));
      setIsEnrollModalOpen(false);
      addToast("Student enrolled successfully!");
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleUnenroll = async () => {
    try {
      await supabase.from('enrollments_2024').delete().eq('id', selectedEnrollment.id);
      setEnrollments(enrollments.filter(e => e.id !== selectedEnrollment.id));
      setAvailableClients([...availableClients, selectedEnrollment.client]);
      addToast("Student unenrolled");
    } catch (error) {
      addToast(error.message, 'error');
    }
  };

  const filteredEnrollments = enrollments.filter(e => 
    e.client?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.client?.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div></div>;

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-20">
      <div className="flex justify-between items-center bg-slate-900 p-6 rounded-2xl border border-slate-800">
        <div className="flex items-center gap-4">
          <Link to="/courses" className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors">
            <SafeIcon icon={FiChevronLeft} className="text-xl" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-white uppercase tracking-tight">{course?.title}</h1>
            <p className="text-xs text-slate-500 font-bold tracking-widest mt-0.5">Student Enrollment Management</p>
          </div>
        </div>
        <button 
          onClick={() => setIsEnrollModalOpen(true)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 transition-all shadow-lg shadow-indigo-600/20"
        >
          <SafeIcon icon={FiPlus} /> Enroll Student
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative w-full md:w-96">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
          <input 
            type="text" 
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-11 pr-4 py-3 text-white focus:ring-2 focus:ring-indigo-500 outline-none"
          />
        </div>
        <div className="flex gap-2">
          <div className="px-4 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs font-bold text-slate-400">
            Total: <span className="text-white">{enrollments.length}</span>
          </div>
          <div className="px-4 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs font-bold text-slate-400">
            Active: <span className="text-emerald-400">{enrollments.filter(e => e.status === 'active').length}</span>
          </div>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
        <table className="w-full text-left">
          <thead className="bg-slate-800/50 border-b border-slate-800 text-slate-500 uppercase text-[10px] font-bold tracking-widest">
            <tr>
              <th className="px-8 py-5">Student</th>
              <th className="px-8 py-5">Progress</th>
              <th className="px-8 py-5">Status</th>
              <th className="px-8 py-5">Enrolled</th>
              <th className="px-8 py-5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {filteredEnrollments.map((enrollment) => (
              <tr key={enrollment.id} className="hover:bg-slate-800/30 transition-colors group">
                <td className="px-8 py-5">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-indigo-500/10">
                      {enrollment.client?.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white">{enrollment.client?.name}</p>
                      <p className="text-[10px] text-slate-500">{enrollment.client?.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-8 py-5">
                  <div className="w-full max-w-[120px] space-y-1.5">
                    <div className="flex justify-between text-[10px] font-bold text-slate-500">
                      <span>{enrollment.progress_percent}%</span>
                    </div>
                    <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${enrollment.progress_percent}%` }}
                        className={`h-full ${enrollment.progress_percent === 100 ? 'bg-emerald-500' : 'bg-indigo-500'}`}
                      />
                    </div>
                  </div>
                </td>
                <td className="px-8 py-5">
                  <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider border ${
                    enrollment.status === 'active' ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' :
                    enrollment.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                    'bg-slate-800 text-slate-500 border-slate-700'
                  }`}>
                    {enrollment.status}
                  </span>
                </td>
                <td className="px-8 py-5 text-xs text-slate-500 font-medium">
                  {new Date(enrollment.enrolled_at).toLocaleDateString()}
                </td>
                <td className="px-8 py-5 text-right">
                  <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button className="p-2 text-slate-500 hover:text-white hover:bg-slate-800 rounded-lg transition-all">
                      <SafeIcon icon={FiMail} />
                    </button>
                    <button 
                      onClick={() => { setSelectedEnrollment(enrollment); setIsUnenrollModalOpen(true); }}
                      className="p-2 text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-all"
                    >
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredEnrollments.length === 0 && (
          <div className="py-20 text-center">
            <div className="w-16 h-16 bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiUsers} className="text-2xl text-slate-500" />
            </div>
            <h3 className="text-white font-bold">No students found</h3>
            <p className="text-slate-500 text-sm mt-1">Try adjusting your search or enroll a new student.</p>
          </div>
        )}
      </div>

      {/* Modals */}
      <FormModal 
        isOpen={isEnrollModalOpen} 
        onClose={() => setIsEnrollModalOpen(false)} 
        title="Enroll Student" 
        onSubmit={handleEnroll} 
        loading={modalLoading}
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-400">Select Client from CRM</label>
            <select name="client_id" required className="w-full bg-slate-800 border-none rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500 outline-none">
              <option value="">Choose a student...</option>
              {availableClients.map(client => (
                <option key={client.id} value={client.id}>{client.name} ({client.email})</option>
              ))}
            </select>
          </div>
          {availableClients.length === 0 && (
            <p className="text-xs text-rose-400 bg-rose-400/10 p-3 rounded-lg">
              No available clients in CRM. Please add clients to your CRM first.
            </p>
          )}
        </div>
      </FormModal>

      <ConfirmModal 
        isOpen={isUnenrollModalOpen} 
        onClose={() => setIsUnenrollModalOpen(false)} 
        onConfirm={handleUnenroll} 
        title="Unenroll Student?" 
        message={`Are you sure you want to remove ${selectedEnrollment?.client?.name} from this course? Their progress data will be preserved but they will lose access.`}
      />
    </div>
  );
}